import { isPerDegreeUnit } from '../../../../../../utils/eumUnit';

export const SPECTRUM_COLOR_SCALE: Array<[number, string]> = [
  [0, '#0f172a'],
  [0.17, '#1e3a5f'],
  [0.33, '#1d6fa4'],
  [0.5, '#22c5c5'],
  [0.67, '#7effa4'],
  [0.83, '#ffd700'],
  [1, '#ff4500'],
];

export const SPECTRUM_COLORS = SPECTRUM_COLOR_SCALE.map(([, color]) => color);

export const SPECTRUM_COLOR_SCALE_WHITE_MIN: Array<[number, string]> = [
  [0, '#ffffff'],
  ...SPECTRUM_COLOR_SCALE.slice(1),
];

export const SPECTRA_CHART_LABELS = {
  directional: 'Omnidirectional energy density [m²/Hz]',
  energyDensity: 'Energy density [m²/deg]',
  frequencyXAxis: 'Frequency (Hz)',
  periodAxis: 'Period (s)',
  frequencyYAxis: 'Direction (°)',
  frequencyChartTitle: 'Frequency spectrum over time',
  directionalChartTitle: 'Directional spectrum over time',
  timeSeriesAxis: 'H0 (m)',
  timeSeriesChartTitle: 'Significant wave height over time',
};

export const getPolarChartTitle = (axisMode: SpectrumAxisMode) =>
  `Energy density spectrum E(${axisMode === 'period' ? 'T' : 'f'}, θ)`;

export const getPolarContourChartTitle = (axisMode: SpectrumAxisMode) =>
  `Energy density spectrum E(${axisMode === 'period' ? 'T' : 'f'}, θ) (contours)`;

// item.unit is a DHI EUM unit name (e.g. "eumUm2SecPerDegree" or
// "eumUm2SecPerRad") - the angular denominator varies per dataset, so the
// label shown next to raw spectrum_2d values (which aren't renormalized,
// unlike derived S_f/S_theta) is derived from it rather than assumed.
export const spectrum2dUnitLabel = (unit: string): string =>
  isPerDegreeUnit(unit) ? 'm²s/deg' : 'm²s/rad';

export const COMPASS_TICK_VALUES = [0, 45, 90, 135, 180, 225, 270, 315];
export const COMPASS_TICK_LABELS = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];
export const DIRECTION_TICK_STEP = 45;

// *** How it works and why ***
// Normalizes a direction axis into ascending [0, 360) order. dfs2 files could
// in principle report a bin center as a negative value if the axis wraps
// across the seam (e.g. -5 instead of 355) - not observed in practice so far,
// but wrapping+sorting defensively guards against it. More importantly, this
// guarantees dirs[0] is the true minimum, which closeDirectionSeam relies on
// when it appends a duplicate at dirs[0] + 360.
export const wrapDirectionAxis = (dirs: number[]): { dirs: number[]; order: number[] } => {
  const wrapped = dirs.map((d) => ((d % 360) + 360) % 360);
  const order = wrapped.map((_, i) => i).sort((a, b) => wrapped[a] - wrapped[b]);
  return { dirs: order.map((i) => wrapped[i]), order };
};

export const reorderByIndex = <T>(values: T[], order: number[]): T[] => order.map((i) => values[i]);

// *** How it works and why ***
// A heatmap infers each row's cell boundary as the midpoint to its neighbors,
// so the first direction bin (nominally centered on 0deg, but 10deg wide -
// i.e. actually covering -5..+5) gets its lower edge extrapolated to -5 and
// clipped by the axis range, while no row exists to fill the mirror sliver at
// the top (355..360). Appending a duplicate of the first row at dirs[0]+360
// fills that top sliver with the same bin's data, so both slivers of the
// seam-straddling bin render with the same value instead of the top being
// empty.
export const closeDirectionSeam = <T>(
  dirs: number[],
  rows: T[]
): { dirs: number[]; rows: T[] } => ({
  dirs: [...dirs, dirs[0] + 360],
  rows: [...rows, rows[0]],
});

export const formatCursorTime = (time: string) => time.replace('T', ' ').slice(0, 16);

// Candidate tick step sizes span both period-scale (seconds, ~0.1-50) and
// frequency-scale (Hz, can be well under 1) magnitudes.
export const computeRadialTicks = (maxValue: number) => {
  const candidates = [0.01, 0.02, 0.05, 0.1, 0.2, 0.5, 1, 2, 5, 10, 20, 50, 100];
  const tickStep = candidates.find((s) => maxValue / s <= 7) || 100;
  return { tickStep, tMax: Math.ceil(maxValue / tickStep) * tickStep };
};

// Bin boundaries for an axis of per-bin center values (periods or freqs).
// Works regardless of whether the values ascend or descend - edges[i] and
// edges[i + 1] bound values[i], extrapolated at both ends. Clamped to >= 0
// since these feed radial coordinates.
//midpoint rule used
// Important: For period, it's rounding
export const computeBinEdges = (values: number[]): number[] => {
  const n = values.length;
  const edges = [values[0] + (values[0] - values[1]) / 2];
  for (let i = 1; i < n; i++) {
    edges.push((values[i - 1] + values[i]) / 2);
  }
  edges.push(values[n - 1] - (values[n - 2] - values[n - 1]) / 2);
  return edges.map((edge) => Math.max(0, edge));
};

export const TIMESERIES_DOMAIN: [number, number] = [0.75, 1];
export const FREQUENCY_DOMAIN: [number, number] = [0.375, 0.715];
export const DIRECTION_DOMAIN: [number, number] = [0, 0.34];

// [n_times][n_bins] → [n_bins][n_times], the orientation Plotly heatmaps expect
export const transpose = (matrix: number[][]): number[][] =>
  matrix[0].map((_, col) => matrix.map((row) => row[col]));

export const findNearestTime = (times: string[], x: string | number): string => {
  const target = new Date(x).getTime();
  return times.reduce((nearest, time) =>
    Math.abs(new Date(time).getTime() - target) < Math.abs(new Date(nearest).getTime() - target)
      ? time
      : nearest
  );
};

export const TIME_CURSOR_LINE = { color: '#facc15', width: 3 };

export const TIMESERIES_LINE_COLOR = '#51628f';

export const getInitialTime = (times: string[]) => times[Math.floor(times.length / 2)];

export const UI_REVISION_TOKEN = 'wave-spectra' as const;

export type SpectrumAxisMode = 'period' | 'frequency';

// Periods span a much wider dynamic range than a linear radial axis can show
// at once (same reason the frequency/period spectrum heatmap uses a log
// y-axis for period mode) - this is the positive floor substituted for the
// 0 s bin edge, since log axes and bar bases can't represent 0.
export const LOG_RADIAL_FLOOR = 0.01;

export const formatPeriodForTooltip = (period: number) =>
  period < 0.1 ? period.toFixed(2) : period.toFixed(1);
